import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, History, X } from 'lucide-react';
import ProductCard from './ProductCard';
import { productsAPI, cartAPI } from '../services/api';
import { getRecent, clearRecent, RECENT_EVENT } from '../services/recentStore';
import { useT } from '../services/i18n';

// Strip of the shopper's last viewed products (localStorage based)
const RecentlyViewed = ({ excludeId, variant = 'page' }) => {
  const { t } = useT();
  const [ids, setIds] = useState(getRecent);
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    const sync = () => setIds(getRecent());
    window.addEventListener(RECENT_EVENT, sync);
    return () => window.removeEventListener(RECENT_EVENT, sync);
  }, []);

  const wanted = ids.filter(id => id !== String(excludeId));

  useEffect(() => {
    if (!wanted.length) { setItems([]); return undefined; }
    let cancelled = false;
    productsAPI.getByIds(wanted)
      .then(list => { if (!cancelled) setItems(list); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted.join(',')]);

  const addToCart = async (id) => {
    setAdding(id);
    try {
      await cartAPI.addItem(id, 1);
      window.dispatchEvent(new Event('cart-updated'));
    } finally {
      setAdding(null);
    }
  };

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 600, behavior: 'smooth' });

  if (!items.length) return null;

  const inner = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-bold text-gray-900 flex items-center gap-2 ${variant === 'home' ? 'text-2xl' : 'text-xl'}`}>
          <History size={20} className="text-orange-500" /> {t('recent.title')}
          <span className="text-sm font-normal text-gray-500">({items.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="recent-clear" onClick={clearRecent} className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1 transition-colors"><X size={12} /> {t('recent.clear')}</button>
          <div className="hidden md:flex gap-2">
            <button type="button" data-testid="recent-prev" onClick={() => scroll(-1)} className="p-2 rounded-full border border-gray-200 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"><ChevronLeft size={18} /></button>
            <button type="button" data-testid="recent-next" onClick={() => scroll(1)} className="p-2 rounded-full border border-gray-200 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
      <div ref={scroller} className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory" data-testid="recent-products-list">
        {items.map(p => (
          <div key={p.id} className="min-w-[260px] max-w-[260px] snap-start" data-testid={`recent-item-${p.id}`}>
            <ProductCard product={p} onAddToCart={addToCart} isAdding={adding === p.id} />
          </div>
        ))}
      </div>
    </>
  );

  if (variant === 'home') {
    return (
      <section className="py-10 bg-gray-50" data-testid="recent-products">
        <div className="max-w-7xl mx-auto px-4">{inner}</div>
      </section>
    );
  }
  return <section className="mt-8" data-testid="recent-products">{inner}</section>;
};

export default RecentlyViewed;
